import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Spinner } from "../../../components/Filtering/Reusable/Spinner";
import "./ResourceDetail.css";
import { useTranslation } from "react-i18next";
import DropDownFrecuency from "../../../components/buttons/DropDownFrecuency";
import DropDownLanguage from "../../../components/buttons/DropDownLanguage";
import DropDownCountry from "../../../components/buttons/DropDownCountry";
import DropDownTheme from "../../../components/buttons/DropDownTheme";
import DropDownOfferStatus from "../../../components/buttons/DropDownOfferStatus";
import DropDownMediaType from "../../../components/buttons/DropDownMediaType";
import BreadCrumb from "../../../components/HeaderComponents/BreadCrum";
import {
  getBackendLangCode,
  normalizeControlledValue,
  toggleSection,
  validateURL,
  getControlledList,
} from "../../../utils/constants";
import ArrowIcon from "../../../utils/ArrowIcon";
import { getUniqueSortedCountries } from "../../../utils/countriesOrder";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import MultiLang from "../../../components/MultiLang/MultiLang";
import {
  setTextForField,
  getTextForField,
  removeLanguageTab,
  addLanguageTab,
  mimeToKey,
} from "../../../utils/formUtils";
import KeywordsInput from "../../../components/Keywords/KeywordsInput";
import { availableLanguages } from "../../../i18n";
import { showErrorAlert } from "../../../utils/showErrorAlert";
import { showSuccessToast } from "../../../utils/showToastAlert";
import { useGetVocabById } from "../../../hooks/vocabularies/useGetVocabById";
import { usePutVocab } from "../../../hooks/vocabularies/usePutVocab";
import { useAuthRoles } from "../../../hooks/useAuthRoles";

const TRANS_FIELDS = [
  "title",
  "metadata.description",
  "metadata.version_notes",
];

function EditResource() {
  const { callApiWithWorker } = useWorkerApiHelper();
  const [isGeneralOpen, setIsGeneralOpen] = useState(true);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [datasetVersion, setDatasetVersion] = useState(null);
  const [, setDatasetModification] = useState(null);
  const [datasetType, setDatasetType] = useState(null);
  const { identifier } = useParams();
  const [, setUrlError] = useState("");
  const [, setUrlDocuError] = useState("");
  const [isUrlValid, setIsUrlValid] = useState(false);
  const [baseLang] = useState(() => i18n.language);
  const currentLang = getBackendLangCode(i18n.language);
  const navigate = useNavigate();
  const [controlledVocabularies, setControlledVocabularies] = useState({
    frequencies: [],
    mediaTypes: [],
    countries: [],
    languages: [],
    themes: [],
    types: [],
    status: [],
  });
  const [languageTabs, setLanguageTabs] = useState([]);
  const [activeLang, setActiveLang] = useState("");
  const [urlLicenseError] = useState("");
  const [urlLandingError, setUrlLandingError] = useState("");
  const { decoded } = useAuthRoles();
  const participantName = decoded.organization;

  const putVocab = usePutVocab({ callApiWithWorker });

  const {
    data: fetchedVocab,
    isFetching: isFetchingVocab,
    isError: isErrorVocab,
    error: vocabError,
  } = useGetVocabById({
    callApiWithWorker,
    id: identifier,
    currentLang: i18n.language,
    enabled: Boolean(identifier),
  });

  useEffect(() => {
    async function fetchVocabularies() {
      const [
        mediaTypes,
        frequencies,
        status,
        countries,
        languages,
        themes,
        types,
      ] = await Promise.all([
        getControlledList(callApiWithWorker, "media-types-ext", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "frequencies", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "distribution-status", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "countries", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "languages", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "data-theme", {
          lang: i18n.language,
        }),
        getControlledList(callApiWithWorker, "resource-types", {
          lang: i18n.language,
        }),
      ]);

      setControlledVocabularies({
        frequencies,
        mediaTypes,
        countries,
        languages,
        themes,
        types,
        status,
      });
    }
    fetchVocabularies();
  }, []);

  const [formData, setFormData] = useState({
    type: "",
    resource_url: "",
    resource_file: "",
    resource_extension: "",
    version_info: "",
    title: [{ language: baseLang, value: "" }],
    metadata: {
      description: [{ language: baseLang, value: "" }],
      version_notes: [{ language: baseLang, value: "" }],
      publisher: "",
      author: [],
      license: "",
      status: "",
      spatial: [],
      theme: [],
      frequency: "",
      language: [],
      documentation: [],
    },
  });

  const [, setIsFormValid] = useState(false);
  const [rawDataset, setRawDataset] = useState(null);

  useEffect(() => {
    const isVaildForm = isUrlValid;
    setIsFormValid(isVaildForm);
  }, [formData, isUrlValid]);

  const [loading, setLoading] = useState(false);

  const sortedCountries = getUniqueSortedCountries(
    controlledVocabularies.countries,
    currentLang,
  );

  const getVocabularyLabel = (item) =>
    item?.label ||
    item?.langName?.[currentLang] ||
    item?.langName?.eng ||
    item?.langName?.spa ||
    Object.values(item?.langName || {}).find(Boolean) ||
    item?.concept_iri ||
    item?.code ||
    "";

  const getVocabularyValue = (item) => item?.concept_iri || item?.code || "";

  const normalizeMediaTypeValue = useCallback(
    (value) => {
      if (!value) return "";

      const mediaTypes = controlledVocabularies.mediaTypes || [];
      if (mediaTypes.length === 0) return value;

      const raw = String(value).toLowerCase().trim().split(";")[0];
      const stripped = raw.replace(/^.*\//, "");
      const normalizedByMime = (mimeToKey(raw) || "").toLowerCase();

      const candidates = [raw, stripped, normalizedByMime].filter(Boolean);

      const match = mediaTypes.find((item) => {
        const code = String(
          item?.concept_iri || item?.code || "",
        ).toLowerCase();
        const labels = [item?.label, ...Object.values(item?.langName || {})]
          .map((label) => String(label || "").toLowerCase())
          .filter(Boolean);

        return candidates.some((candidate) => {
          if (candidate === code) return true;
          if (labels.includes(candidate)) return true;
          return labels.some(
            (label) => label.includes(candidate) || candidate.includes(label),
          );
        });
      });

      return match?.concept_iri || match?.code || value;
    },
    [controlledVocabularies.mediaTypes],
  );

  useEffect(() => {
    if (!fetchedVocab) return;

    const dataset = fetchedVocab;

    setRawDataset(dataset);
    setDatasetVersion(dataset.version);
    setDatasetModification(dataset.modified);
    setDatasetType(dataset.type);
  }, [fetchedVocab]);

  useEffect(() => {
    if (!rawDataset) return;

    const sourceMetadata = rawDataset.metadata ?? rawDataset;

    setFormData({
      type: rawDataset.type || "",
      resource_url: rawDataset.resource_url || "",
      resource_file: rawDataset.resource_file || "",
      resource_extension: normalizeMediaTypeValue(
        rawDataset.resource_extension || sourceMetadata.extension || "",
      ),
      version_info:
        rawDataset.version_info || sourceMetadata.version_info || "",
      title: Array.isArray(rawDataset.title)
        ? rawDataset.title
        : Array.isArray(sourceMetadata.title)
          ? sourceMetadata.title
          : [],
      metadata: {
        description: Array.isArray(sourceMetadata.description)
          ? sourceMetadata.description
          : [],
        version_notes: Array.isArray(sourceMetadata.version_notes)
          ? sourceMetadata.version_notes
          : [],
        publisher: sourceMetadata.publisher || "",
        author: Array.isArray(sourceMetadata.author)
          ? sourceMetadata.author
          : sourceMetadata.author
            ? [sourceMetadata.author]
            : [],
        license: sourceMetadata.license || "",
        status: normalizeControlledValue({
          rawValue: sourceMetadata.status || rawDataset.status,
          vocabulary: controlledVocabularies.status,
          currentLang,
          stripIri: false,
          multiple: false,
        }),
        frequency: normalizeControlledValue({
          rawValue: sourceMetadata.frequency,
          vocabulary: controlledVocabularies.frequencies,
          currentLang,
          multiple: false,
        }),
        language: normalizeControlledValue({
          rawValue: sourceMetadata.language,
          vocabulary: controlledVocabularies.languages,
          currentLang,
          multiple: true,
        }),
        theme: normalizeControlledValue({
          rawValue: sourceMetadata.theme,
          vocabulary: controlledVocabularies.themes,
          currentLang,
          multiple: true,
        }),
        spatial: normalizeControlledValue({
          rawValue: sourceMetadata.spatial,
          vocabulary: controlledVocabularies.countries,
          currentLang,
          multiple: true,
        }),
        documentation: Array.isArray(sourceMetadata.documentation)
          ? sourceMetadata.documentation
          : sourceMetadata.documentation
            ? [sourceMetadata.documentation]
            : [],
      },
    });

    setDatasetVersion(
      rawDataset.version_info ||
        sourceMetadata.version_info ||
        rawDataset.version ||
        null,
    );
    setDatasetModification(rawDataset.modified || null);
    setDatasetType(rawDataset.type || null);
  }, [
    controlledVocabularies.countries,
    controlledVocabularies.frequencies,
    controlledVocabularies.languages,
    controlledVocabularies.status,
    controlledVocabularies.themes,
    currentLang,
    normalizeMediaTypeValue,
    rawDataset,
  ]);

  useEffect(() => {
    if (!formData.resource_extension) return;

    const normalized = normalizeMediaTypeValue(formData.resource_extension);
    if (normalized === formData.resource_extension) return;

    setFormData((prev) => ({
      ...prev,
      resource_extension: normalized,
    }));
  }, [formData.resource_extension, normalizeMediaTypeValue]);

  useEffect(() => {
    if (!rawDataset) return;

    const detectedLangs = new Set();

    TRANS_FIELDS.forEach((field) => {
      const value = field.split(".").reduce((acc, key) => acc?.[key], formData);

      if (Array.isArray(value)) {
        value.forEach((item) => {
          const langCode = item?.lang ?? item?.language;
          if (langCode && langCode !== baseLang) {
            detectedLangs.add(langCode);
          }
        });
      }
    });

    setLanguageTabs(Array.from(detectedLangs));
  }, [rawDataset, baseLang, formData, setLanguageTabs]);

  const handleUnifiedChange = (namePathOrEvent, valueOrEvent) => {
    let namePath, value;

    if (valueOrEvent === undefined) {
      const e = namePathOrEvent;
      if (e && e.target) {
        const { name, type, value: targetValue, files } = e.target;
        namePath = name;
        value = type === "file" ? files[0] || "" : targetValue;
      }
    } else {
      namePath = namePathOrEvent;
      if (valueOrEvent && valueOrEvent.target) {
        const { type, value: targetValue, files } = valueOrEvent.target;
        value = type === "file" ? files[0] || "" : targetValue;
      } else {
        value = valueOrEvent;
      }
    }

    const singleElementArrayFields = [
      "description",
      "version_notes",
      "documentation",
      "author",
    ];
    const multiElementArrayFields = ["language", "spatial", "theme"];
    const arrayFields = [
      ...singleElementArrayFields,
      ...multiElementArrayFields,
    ];

    if (typeof namePath === "string" && namePath.startsWith("metadata.")) {
      const metadataField = namePath.split(".")[1];

      setFormData((prev) => {
        let newValue = value;

        if (arrayFields.includes(metadataField)) {
          if (singleElementArrayFields.includes(metadataField)) {
            if (
              ["description", "version_notes"].includes(metadataField) &&
              typeof newValue === "string"
            ) {
              newValue = newValue.replace(/[\r\n]+/g, " ").trim();
            }
            if (Array.isArray(newValue)) {
              newValue = [newValue.join(" ").trim()];
            } else if (typeof newValue === "string") {
              newValue = [newValue];
            } else {
              newValue = [];
            }
          } else if (multiElementArrayFields.includes(metadataField)) {
            if (Array.isArray(newValue)) {
              newValue = newValue
                .map((v) => (typeof v === "string" ? v.trim() : v))
                .filter(Boolean);
            } else if (typeof newValue === "string") {
              newValue = newValue
                .split(/\r?\n|,/)
                .map((s) => s.trim())
                .filter(Boolean);
            } else {
              newValue = [];
            }
          }
        }

        return {
          ...prev,
          metadata: {
            ...prev.metadata,
            [metadataField]: newValue,
          },
        };
      });
    } else if (typeof namePath === "string") {
      setFormData((prev) => ({
        ...prev,
        [namePath]: value || "",
      }));
    } else {
      console.error("Invalid namePath:", namePath);
    }

    if (["metadata.license", "metadata.documentation"].includes(namePath)) {
      const origin = namePath === "metadata.license" ? "license" : "landing";
      const { valid, error } = validateURL(value, t);
      setUrlErrorByOrigin(origin, error);
      setIsUrlValid(valid);
    }
  };

  const updateResource = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await putVocab.mutateAsync({
        id: identifier,
        payload: {
          resource_id: identifier,
          metadata: {
            title: formData.title,
            version_info: formData.version_info,
            description: formData.metadata.description,
            version_notes: formData.metadata.version_notes,
            author: formData.metadata.author,
            publisher: formData.metadata.publisher,
            license: formData.metadata.license,
            spatial: formData.metadata.spatial,
            theme: formData.metadata.theme,
            frequency: formData.metadata.frequency,
            language: formData.metadata.language,
            status: formData.metadata.status,
            documentation: formData.metadata.documentation,
          },
        },
      });

      showSuccessToast(t("updateSubMessage"), {
        title: t("updateMessage"),
      });
      navigate(-1);
    } catch (error) {
      showErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  const setUrlErrorByOrigin = (origin, message) => {
    switch (origin) {
      case "license":
        setUrlError(message);
        break;
      case "landing":
        setUrlDocuError(message);
        break;
      default:
        console.warn("Unknown error origin:", origin);
    }
  };

  const toggleGeneral = () =>
    toggleSection(setIsGeneralOpen, ".createdivgeneral");
  const toggleDetails = () =>
    toggleSection(setIsDetailsOpen, ".createdivdetails");

  const languageOptions = availableLanguages;

  const getLangLabel = (code) =>
    languageOptions.find((o) => o.code === code)?.label || code;

  const handleAddLanguageTab = (code) => {
    addLanguageTab(
      code,
      baseLang,
      languageTabs,
      TRANS_FIELDS,
      formData,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const handleRemoveLang = (langCode) => {
    removeLanguageTab(
      langCode,
      activeLang,
      TRANS_FIELDS,
      setFormData,
      setLanguageTabs,
      setActiveLang,
    );
  };

  const handleAuthorChange = (nextValues) => {
    const normalized = Array.isArray(nextValues) ? nextValues : [];
    setFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, author: normalized },
    }));
  };

  const handleDocumentationChange = (nextValues) => {
    const normalized = Array.isArray(nextValues) ? nextValues : [];

    const invalid = normalized.find((item) => {
      const value = String(item || "").trim();
      if (!value) return false;
      return !validateURL(value, t).valid;
    });

    setUrlLandingError(invalid ? validateURL(invalid, t).error : "");

    setFormData((prev) => ({
      ...prev,
      metadata: { ...prev.metadata, documentation: normalized },
    }));
  };

  const renderTranslatableInput = (fieldName, placeholder, isTextarea) => {
    const value = getTextForField(formData, fieldName, activeLang, baseLang);
    const lang = activeLang === "" ? baseLang : activeLang;

    if (isTextarea) {
      return (
        <textarea
          rows="4"
          cols="20"
          className="inputoffer fontNoto"
          name={fieldName}
          placeholder={placeholder}
          value={value}
          onChange={(e) =>
            setFormData((prev) =>
              setTextForField(prev, fieldName, lang, e.target.value),
            )
          }
          required={["title", "metadata.description"].includes(fieldName)}
        />
      );
    }

    return (
      <input
        className="inputoffer"
        type="text"
        name={fieldName}
        placeholder={placeholder}
        value={value}
        onChange={(e) =>
          setFormData((prev) =>
            setTextForField(prev, fieldName, lang, e.target.value),
          )
        }
        required={["title"].includes(fieldName)}
      />
    );
  };

  const renderFieldOrDisabled = (FieldComponentOrProps) => {
    const disabled = activeLang !== "";

    if (FieldComponentOrProps && FieldComponentOrProps.custom) {
      const { component: Component, props } = FieldComponentOrProps.custom;
      return <Component {...props} disabled={disabled} />;
    }

    const {
      name,
      value,
      placeholder,
      isTextarea,
      required,
      className,
      onChange,
      ...rest
    } = FieldComponentOrProps;
    if (isTextarea) {
      return (
        <textarea
          rows="4"
          cols="20"
          className={disabled ? "inputofferdisabled" : "inputoffer fontNoto"}
          name={name}
          placeholder={placeholder}
          value={value}
          onChange={handleUnifiedChange}
          required={required}
          disabled={disabled}
        />
      );
    }
    return (
      <input
        className={
          className || (disabled ? "inputofferdisabled" : "inputoffer")
        }
        type="text"
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange || handleUnifiedChange}
        required={required}
        disabled={disabled}
        {...rest}
      />
    );
  };

  if (isFetchingVocab && !rawDataset) {
    return (
      <div className="spinner-container">
        <Spinner size="large" color="var(--main-color)" />
      </div>
    );
  }

  if (isErrorVocab) {
    return (
      <div>{`${t("editResource.errorLoading")}: ${vocabError?.message}`}</div>
    );
  }

  return (
    <section className="grisback pb4">
      <div className="whiteback W100">
        <div>
          <BreadCrumb />
        </div>
      </div>
      <div className="containerflex">
        <div className="whiteback W100 padding2rem">
          <div className="w90">
            <h2 className="tittlecatalog">{t("editResource.name")}</h2>
            <MultiLang
              t={t}
              baseLang={baseLang}
              languageTabs={languageTabs}
              languageOptions={languageOptions}
              activeLang={activeLang}
              addLanguageTab={handleAddLanguageTab}
              removeLanguageTab={handleRemoveLang}
              setActiveLang={setActiveLang}
              getLangLabel={getLangLabel}
            />
          </div>
        </div>
        <div className="W100 containerflex ptb2">
          <div className="w95 bgwhite">
            <div className="creatediv">
              <div
                className="createdivgeneral w100"
                onClick={toggleGeneral}
                style={{ cursor: "pointer" }}
              >
                <h4 className="ptb3lr4">{t("createDataModelDetail.fill")}</h4>
                <ArrowIcon open={isGeneralOpen} />
              </div>
              {isGeneralOpen && (
                <div
                  className="input-fields"
                  style={{ transition: "all 0.3s ease" }}
                >
                  <div className="resource-info">
                    <div className="form-rows">
                      <div className="form-row">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.detail.title")}
                            <span className="required">*</span>
                          </p>
                          {renderTranslatableInput(
                            "title",
                            t("createDataModelDetail.detail.title_placeholder"),
                            false,
                          )}
                        </div>
                        <div className="form-group">
                          <KeywordsInput
                            label={t("createDataModelDetail.metadata.author")}
                            value={formData.metadata.author}
                            onChange={handleAuthorChange}
                            placeholder={t(
                              "editResource.general.author_placeholder",
                            )}
                            disabled={activeLang !== ""}
                            variant="resourceForm"
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.detail.resource_version")}
                            <span className="required">*</span>
                          </p>
                          <input
                            className="inputofferdisabled"
                            disabled
                            style={{ width: 412 }}
                            type="text"
                            placeholder={t(
                              "editResource.general.version_placeholder",
                            )}
                            name="version_info"
                            value={datasetVersion ? datasetVersion : ""}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.detail.format")}
                            <span className="required">*</span>
                          </p>
                          <DropDownMediaType
                            value={formData.resource_extension}
                            onChange={(e) =>
                              handleUnifiedChange("resource_extension", e)
                            }
                            options={controlledVocabularies.mediaTypes.map(
                              (item) => getVocabularyLabel(item),
                            )}
                            optionsValue={controlledVocabularies.mediaTypes.map(
                              (item) => getVocabularyValue(item),
                            )}
                            disabled={true}
                          />
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.metadata.license")}
                          </p>
                          {renderFieldOrDisabled({
                            name: "metadata.license",
                            value: formData.metadata.license,
                            placeholder: t(
                              "createDataModelDetail.detail.resource_url_placeholder",
                            ),
                            isTextarea: false,
                            required: false,
                          })}
                          {urlLicenseError && (
                            <p className="error_text">{urlLicenseError}</p>
                          )}
                        </div>
                        <div className="form-group">
                          <p className="ptitleinput">
                            {t("editResource.general.status")}
                          </p>
                          <DropDownOfferStatus
                            value={formData.metadata.status}
                            onChange={
                              activeLang === ""
                                ? (e) =>
                                    handleUnifiedChange(
                                      "metadata.status",
                                      e.target.value,
                                    )
                                : () => {}
                            }
                            name="metadata.status"
                            options={controlledVocabularies.status.map((item) =>
                              getVocabularyLabel(item),
                            )}
                            translationKey="dropdown.offer_status"
                            optionsValue={controlledVocabularies.status.map(
                              (item) => getVocabularyValue(item),
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div
                className="createdivdetails w100"
                onClick={toggleDetails}
                style={{ cursor: "pointer" }}
              >
                <h4 className="ptb3lr4">
                  {t("editResource.sections.details")}
                </h4>
                <ArrowIcon open={isDetailsOpen} />
              </div>
              {isDetailsOpen && (
                <div
                  className="input-fields"
                  style={{ transition: "all 0.3s ease" }}
                >
                  <div className="details">
                    <div className="form-columns">
                      <div className="form-column">
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.metadata.description")}
                            <span className="required">*</span>
                          </p>
                          {renderTranslatableInput(
                            "metadata.description",
                            t("editResource.details.description_placeholder"),
                            true,
                          )}
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("dataOfferingsDetail.detail.publisher")}
                            <span className="required">*</span>
                          </p>
                          <input
                            className="inputofferdisabled"
                            type="text"
                            placeholder={t(
                              "editResource.details.publisher_placeholder",
                            )}
                            name="metadata.publisher"
                            disabled
                            value={participantName}
                            onChange={(e) =>
                              handleUnifiedChange("metadata.publisher", e)
                            }
                          />
                        </div>
                        <div className="form-group-column">
                          <KeywordsInput
                            label={t(
                              "createDataModelDetail.detail.landing_page_url",
                            )}
                            value={formData.metadata.documentation}
                            onChange={handleDocumentationChange}
                            placeholder={t(
                              "createDataModelDetail.detail.landing_page_url_placeholder",
                            )}
                            disabled={activeLang !== ""}
                            variant="resourceForm"
                          />
                        </div>
                        {urlLandingError && (
                          <p className="error_text">{urlLandingError}</p>
                        )}
                      </div>
                      <div className="form-column">
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("editResource.details.version_notes")}
                          </p>
                          {renderTranslatableInput(
                            "metadata.version_notes",
                            t("editResource.details.version_notes_placeholder"),
                            true,
                          )}
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("editResource.details.update_frequency")}
                          </p>
                          <DropDownFrecuency
                            value={formData.metadata.frequency}
                            onChange={(e) =>
                              handleUnifiedChange("metadata.frequency", e)
                            }
                            options={controlledVocabularies.frequencies.map(
                              (item) => getVocabularyLabel(item),
                            )}
                            optionsValue={controlledVocabularies.frequencies.map(
                              (item) => getVocabularyValue(item),
                            )}
                            disabled
                          />
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.metadata.theme")}
                          </p>
                          <DropDownTheme
                            value={formData.metadata.theme}
                            onChange={
                              activeLang === ""
                                ? (themeArray) =>
                                    handleUnifiedChange(
                                      "metadata.theme",
                                      themeArray,
                                    )
                                : () => {}
                            }
                            options={controlledVocabularies.themes.map((item) =>
                              getVocabularyLabel(item),
                            )}
                            optionsValue={controlledVocabularies.themes.map(
                              (item) => getVocabularyValue(item),
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                      </div>
                      <div className="form-column column-tight">
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.detail.type")}
                            <span className="required">*</span>
                          </p>
                          <input
                            className="inputtimesdisabled"
                            style={{ width: 412 }}
                            type="text"
                            placeholder={t(
                              "editResource.details.type_placeholder",
                            )}
                            name="type"
                            disabled
                            value={datasetType ?? ""}
                          />
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("createDataModelDetail.metadata.language")}
                          </p>
                          <DropDownLanguage
                            value={formData.metadata.language}
                            onChange={
                              activeLang === ""
                                ? (languageArray) =>
                                    handleUnifiedChange(
                                      "metadata.language",
                                      languageArray,
                                    )
                                : () => {}
                            }
                            options={controlledVocabularies.languages.map(
                              (item) => getVocabularyLabel(item),
                            )}
                            optionsValue={controlledVocabularies.languages.map(
                              (item) => getVocabularyValue(item),
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                        <div className="form-group-column">
                          <p className="ptitleinput">
                            {t("editResource.details.geographical_coverage")}
                          </p>
                          <DropDownCountry
                            value={formData.metadata.spatial}
                            onChange={
                              activeLang === ""
                                ? (geographicalCoverageArray) =>
                                    handleUnifiedChange(
                                      "metadata.spatial",
                                      geographicalCoverageArray,
                                    )
                                : () => {}
                            }
                            options={sortedCountries.map((item) => item.label)}
                            optionsValue={sortedCountries.map(
                              (item) => item.value,
                            )}
                            disabled={activeLang !== ""}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="whiteback W100 p2">
        <div className="w90 flexdivbotones">
          <button
            className="boton botoncancel"
            onClick={() => {
              navigate(-1);
            }}
          >
            {t("dataModels.discover.cancel")}
          </button>
          <button
            type="button"
            className="boton botoncapply"
            onClick={updateResource}
          >
            {t("editResource.buttons.update")}
          </button>
        </div>
      </div>
      <div>
        {loading && (
          <div className="loading">
            <Spinner size="large" color="var(--main-color)" />
          </div>
        )}
      </div>
    </section>
  );
}

export default EditResource;
